import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Radio, Wifi, RefreshCw, Image as ImageIcon, Users } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveEventPage = () => {
  const [eventCode, setEventCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isLive, setIsLive] = useState(true);
  const pollingRef = useRef(null);

  const handleJoin = async (e) => {
    e.preventDefault();
    
    if (!eventCode.trim()) {
      toast.error('Please enter an event code');
      return;
    }

    setLoading(true);
    
    try {
      const { data } = await axios.post(`${API}/api/events/join`, {
        event_code: eventCode.toUpperCase()
      });
      
      if (data.valid) {
        setJoined(true);
        setEvent(data.event);
        setPhotos(data.photos.map(p => ({
          ...p,
          image_url: `${API}${p.image_url}`
        })));
        toast.success(`Joined ${data.event.event_name}!`);
        startPolling();
      }
    } catch (e) {
      console.error('Join error:', e);
      toast.error(e.response?.data?.detail || 'Invalid event code');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await axios.post(`${API}/api/events/join`, {
          event_code: eventCode.toUpperCase()
        });
        
        if (data.photos.length > photos.length) {
          const newPhotos = data.photos.map(p => ({
            ...p,
            image_url: `${API}${p.image_url}`
          }));
          setPhotos(newPhotos);
          toast.success('New photos added!');
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 5000); // Poll every 5 seconds
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const toggleLive = () => {
    if (isLive) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    } else {
      startPolling();
    }
    setIsLive(!isLive);
  };

  const refreshPhotos = async () => {
    try {
      const { data } = await axios.post(`${API}/api/events/join`, {
        event_code: eventCode.toUpperCase()
      });
      setPhotos(data.photos.map(p => ({
        ...p,
        image_url: `${API}${p.image_url}`
      })));
      toast.success('Gallery refreshed');
    } catch (e) {
      toast.error('Failed to refresh');
    }
  };

  if (joined && event) {
    return (
      <div className="min-h-screen bg-black pt-24" data-testid="live-event-gallery">
        {/* Header */}
        <section className="py-8 border-b border-[#333]">
          <div className="container-luxury">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`}></div>
                  <span className="text-[#C9A14A] uppercase tracking-widest text-xs">
                    {isLive ? 'Live' : 'Paused'}
                  </span>
                </div>
                <h1 className="font-['Playfair_Display'] text-3xl text-white">
                  {event.event_name}
                </h1>
                <p className="text-gray-400 text-sm">Code: {event.event_code}</p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-gray-400">
                  <ImageIcon className="w-5 h-5" />
                  <span>{photos.length} photos</span>
                </div>
                <Button
                  onClick={toggleLive}
                  className={`${isLive ? 'bg-red-500/20 text-red-400 border-red-500/50' : 'bg-[#333] text-white border-[#444]'} border px-4 py-2`}
                  data-testid="toggle-live-button"
                >
                  <Wifi className="w-4 h-4 mr-2" />
                  {isLive ? 'Pause Live' : 'Resume Live'}
                </Button>
                <Button
                  onClick={refreshPhotos}
                  className="bg-[#222] text-white border border-[#333] hover:border-[#C9A14A] px-4 py-2"
                  data-testid="refresh-button"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Photo Grid */}
        <section className="py-8">
          <div className="container-luxury">
            {photos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id || index}
                    className="aspect-square overflow-hidden group relative"
                    data-testid={`live-photo-${index}`}
                  >
                    <img
                      src={photo.image_url}
                      alt={`Event photo ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    {index === 0 && photos.length > 1 && (
                      <div className="absolute top-2 left-2 bg-[#C9A14A] text-black text-xs px-2 py-1 uppercase tracking-wider">
                        New
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-6 bg-[#1A1A1A] rounded-full flex items-center justify-center">
                  <Radio className="w-10 h-10 text-[#C9A14A] animate-pulse" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">
                  Waiting for Photos
                </h3>
                <p className="text-gray-400 text-sm">
                  Photos will appear here in real-time as they are uploaded.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="live-event-page">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#C9A14A]/5 to-transparent"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C9A14A]/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-luxury relative z-10">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-8 bg-[#C9A14A]/10 rounded-full flex items-center justify-center animate-pulse-gold">
              <Radio className="w-10 h-10 text-[#C9A14A]" />
            </div>

            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-light text-white mb-4">
              Live Event
            </h1>
            <p className="text-gray-400 mb-10">
              Join an event to see photos in real-time as they're captured
            </p>

            <form onSubmit={handleJoin} className="space-y-6">
              <div className="card-luxury p-6">
                <Input
                  type="text"
                  value={eventCode}
                  onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                  placeholder="EVT-XXXXXX"
                  className="text-center text-xl tracking-widest bg-transparent border-b-2 border-[#333] focus:border-[#C9A14A] rounded-none py-4 text-white placeholder:text-gray-600"
                  maxLength={10}
                  data-testid="event-code-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-gold"
                disabled={loading}
                data-testid="join-event-button"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                    Joining...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Wifi className="w-4 h-4" />
                    Join Event
                  </div>
                )}
              </Button>
            </form>

            <p className="text-gray-500 text-xs mt-6">
              Event codes are provided by the event organizer or photographer.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LiveEventPage;
